import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Hook pour gérer le Service Worker et le statut en ligne
export function useServiceWorker() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [swRegistration, setSwRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [pendingSyncs, setPendingSyncs] = useState(0);

  // Enregistrer le Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered');
          setSwRegistration(registration);

          // Vérifier les mises à jour
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker?.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          });
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });

      // Écouter les messages du SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_SUCCESS') {
          toast.success('Synchronisation réussie', {
            description: 'Vos modifications ont été enregistrées'
          });
          getPendingSyncs();
        }
      });
    }
  }, []);

  // Gérer le statut en ligne/hors-ligne
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Connexion rétablie', {
        description: 'Synchronisation en cours...'
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Mode hors-ligne', {
        description: 'Vos modifications seront synchronisées quand vous serez en ligne'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Obtenir le nombre de syncs en attente
  const getPendingSyncs = useCallback(() => {
    if (navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        setPendingSyncs(event.data.pendingCount || 0);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_QUEUE_STATUS' },
        [messageChannel.port2]
      );
    }
  }, []);

  // Appliquer la mise à jour
  const applyUpdate = useCallback(() => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, [swRegistration]);

  // Vider le cache
  const clearCache = useCallback(() => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      toast.success('Cache vidé');
    }
  }, []);

  // Vérifier les syncs en attente périodiquement
  useEffect(() => {
    getPendingSyncs();
    const interval = setInterval(getPendingSyncs, 30000);
    return () => clearInterval(interval);
  }, [getPendingSyncs]);

  return {
    isOnline,
    updateAvailable,
    pendingSyncs,
    applyUpdate,
    clearCache,
    swRegistration
  };
}

// Composant indicateur de statut
export function ConnectionStatus() {
  const { isOnline, updateAvailable, pendingSyncs, applyUpdate } = useServiceWorker();

  if (updateAvailable) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg">
        <RefreshCw className="w-4 h-4" />
        <span className="text-sm">Mise à jour disponible</span>
        <Button size="sm" variant="secondary" onClick={applyUpdate}>
          Mettre à jour
        </Button>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
        <WifiOff className="w-4 h-4" />
        <span className="text-sm">Mode hors-ligne</span>
        {pendingSyncs > 0 && (
          <Badge variant="secondary" className="bg-white/20 text-white">
            {pendingSyncs} en attente
          </Badge>
        )}
      </div>
    );
  }

  // Afficher brièvement quand on revient en ligne avec des syncs
  if (pendingSyncs > 0) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
        <Cloud className="w-4 h-4 animate-bounce" />
        <span className="text-sm">Synchronisation...</span>
      </div>
    );
  }

  return null;
}

// Petit badge pour la barre de navigation
export function ConnectionBadge({ className = '' }) {
  const { isOnline, pendingSyncs } = useServiceWorker();

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {isOnline ? (
        <Wifi className="w-3 h-3 text-green-500" />
      ) : (
        <>
          <WifiOff className="w-3 h-3 text-amber-500" />
          {pendingSyncs > 0 && (
            <span className="text-[10px] text-amber-500 font-medium">
              ({pendingSyncs})
            </span>
          )}
        </>
      )}
    </div>
  );
}

export default ConnectionStatus;
